import { useState } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MarkdownProse } from "@/components/ui/markdown-prose";
import { Textarea } from "@/components/ui/textarea";
import { Timestamp } from "@/components/ui/timestamp";
import { cn } from "@/lib/utils";
import { iconFor, sentence } from "./timeline-formatters";
import type { Entry } from "./timeline-types";

interface TimelineItemProps {
  entry: Entry;
  projectId: string;
  names: Map<string, string | null>;
  isOwn: boolean;
  onChanged: () => void;
}

export function TimelineItem({
  entry,
  projectId,
  names,
  isOwn,
  onChanged,
}: TimelineItemProps) {
  const Icon = iconFor(entry.kind);
  const [expanded, setExpanded] = useState(false);

  return (
    <li
      id={entry.id ? `comment-${entry.id}` : undefined}
      className={cn(
        "relative pl-6 transition-all duration-300",
        "target:bg-muted/30 target:ring-1 target:ring-border target:rounded-md target:py-1.5 target:pr-2",
      )}
    >
      <span
        className="absolute -left-2.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-xs"
        aria-hidden="true"
      >
        <Icon className="h-3 w-3" />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm">
          {sentence(entry, projectId, names)}{" "}
          <Timestamp
            date={entry.createdAt}
            className="text-xs tabular-nums text-muted-foreground"
          />
          {entry.editedAt && (
            <span className="text-xs text-muted-foreground"> · edited</span>
          )}
        </p>
        {entry.kind === "comment" ? (
          <CommentBody entry={entry} isOwn={isOwn} onChanged={onChanged} />
        ) : (
          entry.body &&
          entry.kind !== "outbound_comment" && (
            <div className="space-y-1">
              <p
                className={cn(
                  "text-sm text-muted-foreground",
                  !expanded && "line-clamp-2",
                )}
              >
                {entry.body}
              </p>
              {entry.body.length > 120 && (
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="text-xs font-medium text-foreground underline-offset-4 hover:underline"
                >
                  {expanded ? "Show less" : "Show full observation"}
                </button>
              )}
            </div>
          )
        )}
      </div>
    </li>
  );
}

function CommentBody({
  entry,
  isOwn,
  onChanged,
}: {
  entry: Entry;
  isOwn: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.body ?? "");

  const edit = trpc.workflow.comment.edit.useMutation({
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
    onError: (err) => toastTrpcError(err, "Failed to edit note"),
  });

  const remove = trpc.workflow.comment.delete.useMutation({
    onSuccess: onChanged,
    onError: (err) => toastTrpcError(err, "Failed to delete note"),
  });

  if (editing) {
    const id = `edit-${entry.id}`;
    const handleEditSubmit = (e?: React.FormEvent) => {
      e?.preventDefault();
      if (draft.trim() && !edit.isPending) {
        edit.mutate({ commentId: entry.id, body: draft.trim() });
      }
    };

    return (
      <form className="space-y-2" onSubmit={handleEditSubmit}>
        <Label htmlFor={id} className="sr-only">
          Edit note
        </Label>
        <Textarea
          id={id}
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              handleEditSubmit();
            }
          }}
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Press ⌘Enter to save</span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={edit.isPending || !draft.trim()}
            >
              {edit.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-1">
      <MarkdownProse
        content={entry.body ?? ""}
        className="max-w-[70ch] text-base text-foreground"
      />
      {isOwn && (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            Edit<span className="sr-only"> note</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => remove.mutate({ commentId: entry.id })}
            disabled={remove.isPending}
          >
            Delete<span className="sr-only"> note</span>
          </Button>
        </div>
      )}
    </div>
  );
}
